import type React from "react"
import { useEffect, useMemo, useRef, useState } from "react"
import { motion } from "framer-motion"
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js"
import type { PressData } from "../../models/listpress"
import "react-datepicker/dist/react-datepicker.css"
import { createAnnonceTemp } from "../../services/Annonce_service"
import visa from "../../image/visa.png"
import master from "../../image/mastercard.webp"
import amex from "../../image/banque.png"
import "../style/region.css"
import "../style/payement.css"
import { useNavigate } from "react-router-dom"
import useAppContext from "../../hooks/useAppContext"
import type { AppContextType } from "../../context/AppContext"
import type { TabStypeItem } from "../../models/typeAnnonce"
import AnonymousModalPaiement from "./AnonymousModalPaiement"
import { createFreeAnnonce, createTransaction } from "../../services/Payment_service"
import { updateAvoirsStatus } from "../../services/Avoir_service"
import { claimDiscount } from "../../services/Solde_service"
import { toast } from "react-toastify"
import useUserLoyaltyCommission from "../../hooks/useLoyaltyCommission"
import { getUserDetails } from "../../services/User_service";
import type { UserModel } from "../../models/UserModel";
import Swal from 'sweetalert2';
import { Alert } from "antd";
import { usePointsCalculator } from "../../hooks/usePointsCalculator"
import PointsRewardAnimation from "./PointsRewardAnimation"
import { io, type Socket } from "socket.io-client";

interface PaymentProps {
  selectedSupport: PressData | null
  txtAnnonce: string
  entete: string
  floid: string
  annonceur: string
  denomination: string
  reference: string
  dateParution: Date
  tv_intra: number
  selectedAnnouncementType: TabStypeItem
  paymentRef: string | null
  updateTxtAnnonce: (newTxt: string) => void
  updateFloid: (newTxt: string) => void
  updateAnnonceur: (newTxt: string) => void
  updateReference: (newTxt: string) => void
  updateDateParution: (newTxt: Date) => void
  resetPressSupport: () => void
  hasFreeAnnouncement?: boolean
  pointsDiscountApplied?: number
  avoirsDiscountApplied?: number
  usedAvoirsIds?: number[]
  paymentAmount?: number;

  discountPercentageAmount?: number;
  pointsDiscountAmount?: number;
  creditNoteDiscountAmount?: number;
  totalDiscountAmount?: number;

  discountClaimed?: boolean
  discountSentToClient?: boolean
  //return to validation page
  onBack?: () => void;

}

const getPrixHT = (support?: PressData | null): number => {
  if (!support) return 0
  if (typeof support.prixMajoreHT === "number") return support.prixMajoreHT
  return Number.parseFloat(support.prixHT || "0")
}

const getPrixTTC = (support?: PressData | null): number => {
  if (!support) return 0
  if (typeof support.prixMajoreTTC === "number") return support.prixMajoreTTC
  return Number.parseFloat(support.prixTTC || "0")
}

function calculeRemisePourPaiement({
  commission,
  codePromo,
}: {
  commission: number | null
  codePromo: number | null
}) {
  let remisePourPaiement = 0;
  if (commission != null && codePromo != null) {
    remisePourPaiement = commission + codePromo;
  } else if (commission != null) {
    remisePourPaiement = commission;
  } else if (codePromo != null) {
    remisePourPaiement = codePromo;
  }
  if (remisePourPaiement > 100) remisePourPaiement = 100;
  const isGratuit = remisePourPaiement === 100;
  return { remisePourPaiement, isGratuit };
}

const Payment: React.FC<PaymentProps> = ({
  onBack,
  selectedSupport,
  txtAnnonce,
  denomination,
  tv_intra,
  selectedAnnouncementType,
  floid,
  entete,
  annonceur,
  reference,
  updateDateParution,
  paymentRef,
  hasFreeAnnouncement = false,
  pointsDiscountApplied = 0,
  avoirsDiscountApplied = 0,
  usedAvoirsIds = [],
  paymentAmount = 0,

  discountPercentageAmount = 0,
  pointsDiscountAmount = 0,
  creditNoteDiscountAmount = 0,
  totalDiscountAmount = 0,

  discountClaimed = false,
  discountSentToClient = false
}) => {

  // console.log("Détails remises:", {
  //   discountPercentageAmount,
  //   pointsDiscountAmount,
  //   creditNoteDiscountAmount,
  //   totalDiscountAmount
  // });

  const {
    companyName,
    pressSupportCity,
    pressSupportPostalCode,
    discountAmount,
    discountId,
    denomination: contextDenomination,
    texteAnnonce,
    pointsToUse
  } = useAppContext() as AppContextType

  const [isModalVisible, setIsModalVisible] = useState(false)
  const [userDetails, setUserDetails] = useState<UserModel | null>(null)
  const [userDetailsLoading, setUserDetailsLoading] = useState(true)
  const [startDate, setStartDate] = useState<Date | null>(new Date())
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [amount, setAmount] = useState<string>("")
  const [message, setMessage] = useState<string>("")
  const [loading, setLoading] = useState<boolean>(false)
  const [showAlert, setShowAlert] = useState<boolean>(false)
  const [processingFreeAnnouncement, setProcessingFreeAnnouncement] = useState<boolean>(false)
  const [animationKey, setAnimationKey] = useState(0)
  const [shouldTriggerPayment, setShouldTriggerPayment] = useState(false);

  const [isWaitingValidation, setIsWaitingValidation] = useState(false);
  const isWaitingValidationRef = useRef(isWaitingValidation);
  const annonceTempRef = useRef<{ [key: string]: string } | null>(null)

  const [isPaymentElementComplete, setIsPaymentElementComplete] = useState(false);

  const navigate = useNavigate()
  const isRedirectingRef = useRef(false)
  const hasRedirectedRef = useRef(false) // empêche double redirection
  const socketRef = useRef<Socket | null>(null)
  const timeoutRef = useRef<number | null>(null)

  const userId = localStorage.getItem("userId")
  const userIdNumber = userId ? Number.parseInt(userId, 10) : 0

  const stripe = useStripe()
  const elements = useElements()

  // Met à jour le ref pour le timeout (évite closure stale)
  useEffect(() => {
    isWaitingValidationRef.current = isWaitingValidation
  }, [isWaitingValidation])

  // Écoute du webhook via Socket.IO (avec reconnexion)
  useEffect(() => {
    const socket = io(process.env.REACT_APP_API_BASE || "http://localhost:5200", {
      transports: ["polling", "websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    socket.on("payment_status", async (data: any) => {
      console.log("[SOCKET] Événement paiement reçu :", data);

      if (data.paymentIntentId !== paymentRef) {
        console.log(`⚠️ Ce n'est pas notre paiement`);
        return;
      }

      if (hasRedirectedRef.current) {
        console.log("⚠️ Redirection déjà effectuée, événement ignoré");
        return;
      }

      try {
        if (data.status === "succeeded") {
          hasRedirectedRef.current = true;

          // Marquer les avoirs comme utilisés
          if (usedAvoirsIds && usedAvoirsIds.length > 0) {
            try {
              await markAvoirsAsUsed(usedAvoirsIds);
            } catch (err) {
              console.error("❌ Erreur marquage avoirs:", err);
            }
          }

          if (data.alreadyExists) {
            toast.info("Paiement déjà traité, redirection...");
          } else {
            toast.success("Paiement confirmé ! Création de votre annonce...");
          }

          if (timeoutRef.current) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }

          setTimeout(() => {
            navigate("/home/gestion-annonces");
          }, 1000);

        } else if (data.status === "failed") {
          console.error("❌ Paiement échoué");
          setIsWaitingValidation(false);
          hasRedirectedRef.current = true;

          // ✅ Redirection différente selon le moyen de paiement
          if (data.isKlarna) {
            toast.error("Paiement Klarna échoué. Votre annonce a été sauvegardée en brouillon.", {
              autoClose: 3000,
            });

            setTimeout(() => {
              navigate("/home/draft");
            }, 2000);
          } else {
            toast.error("Paiement échoué. Veuillez réessayer.");
            // Reste sur la page de paiement pour les autres moyens
          }

          if (timeoutRef.current) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }

        } else if (data.status === "canceled") {
          console.warn("🚫 Paiement annulé");
          setIsWaitingValidation(false);
          hasRedirectedRef.current = true;

          // ✅ Redirection différente selon le moyen de paiement
          if (data.isKlarna) {
            toast.warning("Paiement Klarna annulé. Redirection vers vos brouillons...", {
              autoClose: 2500,
            });

            setTimeout(() => {
              navigate("/home/draft");
            }, 1500);
          } else {
            toast.warning("Paiement annulé");
            // Reste sur la page de paiement pour les autres moyens
          }

          if (timeoutRef.current) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }

        } else if (data.status === "error") {
          console.error("❌ Erreur paiement:", data.error);
          setIsWaitingValidation(false);
          toast.error(`Erreur lors du paiement: ${data.error || "Erreur inconnue"}`);

          if (timeoutRef.current) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
        }
      } catch (error) {
        console.error("❌ Erreur dans socket payment_status handler:", error);
        setIsWaitingValidation(false);
        toast.error("Une erreur est survenue lors du traitement du paiement");
      }
    });

    return () => {
      // console.log("🔌 Déconnexion du Socket.IO");
      try {
        socket.off("payment_status");
        socket.disconnect();
      } catch (e) {
        console.error("Erreur lors de la déconnexion socket:", e);
      }
      socketRef.current = null;

      // Clear timeout si le composant est démonté
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [navigate, usedAvoirsIds, paymentRef]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const user = await getUserDetails()
        if (user) {
          setUserDetails(user)
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des infos utilisateur :", error)
      } finally {
        setUserDetailsLoading(false)
      }
    }
    fetchUserDetails()
  }, [])

  useEffect(() => {
    if (typeof paymentAmount === "number" && paymentAmount > 0) {
      setAmount(paymentAmount.toFixed(2));
    }
  }, [paymentAmount]);

  const showAnonymousModal = () => {
    setIsModalVisible(true)
  }

  const handleModalClose = () => {
    setIsModalVisible(false)
  };

  const {
    commission,
    loading: commissionLoading,
    error: commissionError,
  } = useUserLoyaltyCommission(userIdNumber)


  const text = commission?.commissionPercentage
    ? `"Pour vous remercier de votre fidélité, notre équipe a le plaisir de vous offrir une remise exceptionnelle de ${commission.commissionPercentage} % pour la création de votre annonce."`
    : ""

  const typewriterVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.03,
        delayChildren: 0,
      },
    },
  }

  const letterVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.1,
      },
    },
  }

  const hasActiveCommission = () => {
    return commission && commission.isActive && commission.commissionPercentage > 0
  }

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isRedirectingRef.current) {
        setShowAlert(true)
        event.preventDefault()
        event.returnValue = ""
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "F5" || ((event.ctrlKey || event.metaKey) && event.key === "r") || (event.ctrlKey && event.key === "F5")) {
        setShowAlert(true)
        event.preventDefault()
        Swal.fire({
          icon: "warning",
          title: "Action interdite",
          text: "Il est strictement interdit de rafraîchir cette page, car cela interrompra la procédure de paiement.",
          showCancelButton: true,
          confirmButtonText: "Rester sur la page",
          cancelButtonText: "Actualiser et quitter",
          confirmButtonColor: "#2a919d",
          cancelButtonColor: "#e67921",
          reverseButtons: true,
        }).then((result) => {
          if (result.dismiss === Swal.DismissReason.cancel) {
            window.location.href = "/home"
          }
        })
        return false
      }
    }

    document.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [])

  useEffect(() => {
    if (hasFreeAnnouncement && paymentRef && !processingFreeAnnouncement) {
      setProcessingFreeAnnouncement(true)
      handleCreateFreeAnnouncement()
    }
  }, [paymentRef, hasFreeAnnouncement])


  const formatDateForRequest = (date: Date | null): string => {
    if (!date) return ""
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, "0")
    const day = date.getDate().toString().padStart(2, "0")
    return `${year}${month}${day}`
  }

  // Fonction pour marquer les avoirs comme utilisés
  const markAvoirsAsUsed = async (avoirIds: number[]) => {
    if (avoirIds.length > 0) {
      try {
        await updateAvoirsStatus(avoirIds, "utilisé")
        // console.log("Avoirs marqués comme utilisés:", avoirIds)

        // Émettre un événement pour mettre à jour les autres composants
        window.dispatchEvent(new CustomEvent('avoirsUsed', {
          detail: { avoirIds }
        }))

      } catch (error) {
        console.error("Erreur lors de la mise à jour des avoirs:", error)
        // Ne pas faire échouer le processus si la mise à jour échoue
      }
    }
  }

  const handleCreateFreeAnnouncement = async () => {
    if (!paymentRef) {
      toast.error("Référence de paiement manquante")
      return
    }
    setLoading(true)
    try {
      const response = await createFreeAnnonce(paymentRef)
      if (response.success) {
        // Marquer les avoirs comme utilisés si applicable
        if (usedAvoirsIds.length > 0) {
          await markAvoirsAsUsed(usedAvoirsIds)
        }

        toast.success("Votre annonce gratuite a été créée avec succès!")
        navigate("/home/gestion-annonces")
      } else {
        toast.error(response.message || "Une erreur est survenue lors de la création de l'annonce gratuite")
        setProcessingFreeAnnouncement(false)
      }
    } catch (error: any) {
      toast.error(error.message || "Une erreur est survenue lors de la création de l'annonce gratuite")
      setProcessingFreeAnnouncement(false)
    } finally {
      setLoading(false)
    }
  }

  const handleTempAnnounceCreation = async () => {
    if (!selectedSupport) {
      toast.error("Veuillez sélectionner un support avant de valider.");
      return null;
    }

    if (!startDate) {
      toast.error("Veuillez sélectionner une date de parution.");
      return null;
    }

    setIsSubmitting(true);
    const formattedDate = formatDateForRequest(startDate);

    const prixHT = getPrixHT(selectedSupport);
    const prixTTC = getPrixTTC(selectedSupport);

    const commissionVal = hasActiveCommission() ? commission?.commissionPercentage || 0 : 0;
    const codePromoVal = discountAmount || 0;
    const { remisePourPaiement, isGratuit } = calculeRemisePourPaiement({
      commission: commissionVal,
      codePromo: codePromoVal,
    });

    // ✅ Utiliser directement paymentAmount qui contient déjà la bonne logique de Validation
    // (pas de remises si discountClaimed ou discountSentToClient)
    const montantApresAvoirs = paymentAmount > 0 ? paymentAmount : 0;
    const montantHT = isGratuit ? 0 : prixHT * (1 - remisePourPaiement / 100);

    // Utilisation des props avec fallback à 0
    // Garder les remises toujours, indépendamment de discountClaimed
    const discountPercentageAmountProp = discountPercentageAmount ?? 0;
    const pointsDiscountAmountProp = pointsDiscountAmount ?? 0;
    const creditNoteDiscountAmountProp = creditNoteDiscountAmount ?? 0;

    // Calcul du total directement à partir des props
    const totalDiscountAmountProp = discountPercentageAmountProp + pointsDiscountAmountProp + creditNoteDiscountAmountProp;

    try {
      const announceData = {
        texte: texteAnnonce || txtAnnonce,
        numdossier: "",
        numannonce: "",
        paymentRef,
        referenceClient: reference,
        annonceur: contextDenomination || annonceur,
        annonce: texteAnnonce || txtAnnonce,
        departement: selectedSupport.departement,
        jal: selectedSupport.id,
        nJustif: "",
        type_annonce: selectedAnnouncementType.sSType,
        demande: formattedDate,
        type: selectedAnnouncementType.nType,
        stype: selectedAnnouncementType.nSType,
        entete,
        floid,
        textAnnonce: texteAnnonce || txtAnnonce,
        denomination: contextDenomination || denomination,
        reference_client: companyName.clientReference,
        userId,
        tv_intra,
        postalCode: pressSupportPostalCode,
        city: pressSupportCity,
        isFree: hasFreeAnnouncement,
        montantHTannonce: montantHT,
        montantTTCannonce: montantApresAvoirs,
        montantFinalTTC: montantApresAvoirs,
        PromoCodeId: discountId,
        commission_percentage: commissionVal,
        usedPoints: pointsToUse,
        usedAvoirsIds: usedAvoirsIds,
        creditNoteAmount: avoirsDiscountApplied,
        legalMoTTC: getPrixTTC(selectedSupport),

        discountPercentageAmount: discountPercentageAmountProp,
        pointsDiscountAmount: pointsDiscountAmountProp,
        creditNoteDiscountAmount: creditNoteDiscountAmountProp,
        totalDiscountAmount: totalDiscountAmountProp,

      };


      localStorage.setItem('montantFinalTTC', String(montantApresAvoirs));

      // console.log("Données de l'annonce temporaire:", announceData);

      const response = await createAnnonceTemp(announceData, imageFile);

      if (discountClaimed && totalDiscountAmount && totalDiscountAmount > 0) {
        try {
          await claimDiscount({
            totalAmount: totalDiscountAmount,
            details: {
              percentageDiscount: discountPercentageAmountProp,
              pointsDiscount: pointsDiscountAmountProp,
              creditNoteDiscount: creditNoteDiscountAmountProp,
              usedPoints: pointsToUse,
              usedAvoirsIds: usedAvoirsIds,
            },
          });
          // console.log(`✅ Réclamation de ${totalDiscountAmount}€ appliquée avec succès`);
        } catch (error) {
          console.error("❌ Erreur lors de l'application de la réclamation:", error);
          // On ne bloque pas le paiement si la réclamation échoue
          toast.warning("La réclamation n'a pas pu être appliquée, mais votre paiement a été validé.");
        }
      }

      await createTransaction({
        amount: Math.round(montantApresAvoirs * 100),
        paymentRef: paymentRef || "",
        pointsUsed: pointsToUse
      })

      if (!response || response.error) {
        toast.error("Erreur lors de la création de l'annonce.");
        return null;
      }

      return { ...response.data, montant: montantApresAvoirs };
    } catch (error) {
      console.error(error);
      toast.error("Une erreur est survenue lors de la création de l'annonce temporaire.");
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };


  const handlePayment = async () => {
    setMessage("");
    setLoading(true);

    if (!hasFreeAnnouncement && (!stripe || !elements)) {
      setLoading(false);
      toast.error("Stripe n'est pas encore chargé");
      return;
    }

    if (!selectedSupport) {
      setLoading(false);
      toast.error("Veuillez sélectionner un support avant de valider.");
      return;
    }

    try {
      if (hasFreeAnnouncement && paymentRef) {
        await handleCreateFreeAnnouncement();
        return;
      }

      const annonceResult = await handleTempAnnounceCreation();
      annonceTempRef.current = annonceResult || null;

      if (!annonceResult) {
        setMessage("Création de l'annonce échouée, paiement annulé.");
        setLoading(false);
        return;
      }

      if (stripe && elements) {
        // Prévenir l'utilisateur qu'on est en train de rediriger / confirmer
        isRedirectingRef.current = true;

        const { error, paymentIntent } = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/klarna-return`,
          },
          redirect: "if_required",
        });

        if (error) {
          isRedirectingRef.current = false;
          console.error("[DEBUG] Erreur paiement Stripe :", error);
          setMessage(`Erreur de paiement: ${error.message}`);
          toast.error(`Erreur de paiement: ${error.message}`);
          setLoading(false);
          return;
        }

        // On attend le webhook → pas de redirection automatique ici
        setIsWaitingValidation(true);
        setMessage("Votre paiement est en attente de validation. Veuillez confirmer dans votre application bancaire.");
        toast.info("Paiement initié. Veuillez confirmer dans votre banque si nécessaire.");

        // Timeout de sécurité (60s). Si toujours en attente => redirection manuelle
        if (timeoutRef.current) {
          window.clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = window.setTimeout(() => {
          if (!hasRedirectedRef.current && isWaitingValidationRef.current) {
            console.warn("⏱️ Timeout: redirection manuelle");
            toast.warning("Le traitement prend plus de temps que prévu. Vérifiez vos annonces.");
            hasRedirectedRef.current = true;
            navigate("/home/gestion-annonces");
          }
          timeoutRef.current = null;
        }, 60000);
      }
    } catch (error: any) {
      setMessage("Erreur lors de la demande de paiement.");
      toast.error(error.message || "Erreur lors de la demande de paiement.");
    } finally {
      setLoading(false);
    }
  };

  // Nouveau hook pour calculer les points à gagner
  const montantFinalTTC = useMemo(() => {
    if (!selectedSupport || commissionLoading) return 0;

    const prixTTC = getPrixTTC(selectedSupport);
    const commissionVal = hasActiveCommission() ? commission?.commissionPercentage || 0 : 0;
    const codePromoVal = discountAmount || 0;
    const { remisePourPaiement, isGratuit } = calculeRemisePourPaiement({
      commission: commissionVal,
      codePromo: codePromoVal,
    });

    // Si discountClaimed ou discountSentToClient, ne pas appliquer les remises
    let montantFinal = isGratuit
      ? 0
      : discountClaimed || discountSentToClient
        ? prixTTC
        : prixTTC * (1 - remisePourPaiement / 100) - pointsDiscountApplied - avoirsDiscountApplied;

    return Math.max(0, montantFinal);
  }, [selectedSupport, discountAmount, commission, commissionLoading, pointsDiscountApplied, avoirsDiscountApplied, discountClaimed, discountSentToClient]);

  const { calculatePoints, loading: pointsLoading } = usePointsCalculator();

  const pointsToEarn = useMemo(() => {
    return calculatePoints(montantFinalTTC);
  }, [calculatePoints, montantFinalTTC]);


  useEffect(() => {
    if (selectedSupport && !commissionLoading) {
      const prixTTC = getPrixTTC(selectedSupport)
      const commissionVal = hasActiveCommission() ? commission?.commissionPercentage || 0 : 0
      const codePromoVal = discountAmount || 0
      const { remisePourPaiement, isGratuit } = calculeRemisePourPaiement({
        commission: commissionVal,
        codePromo: codePromoVal,
      })

      // Si discountClaimed ou discountSentToClient, ne pas appliquer les remises
      let montantFinal = isGratuit
        ? 0
        : discountClaimed || discountSentToClient
          ? prixTTC
          : prixTTC * (1 - remisePourPaiement / 100) - pointsDiscountApplied - avoirsDiscountApplied
      if (montantFinal < 0) montantFinal = 0
      setAmount(montantFinal.toFixed(2))
    } else {
      setAmount("")
    }
  }, [selectedSupport, discountAmount, commission, commissionLoading, pointsDiscountApplied, avoirsDiscountApplied, discountClaimed, discountSentToClient])

  useEffect(() => {
    if (!text || !commission?.commissionPercentage) return
    const interval = setInterval(
      () => {
        setAnimationKey((prev) => prev + 1)
      },
      text.length * 30 + 4000,
    )
    return () => clearInterval(interval)
  }, [text, commission])


  useEffect(() => {

    if (shouldTriggerPayment) {

      setShouldTriggerPayment(false);

      document.getElementById("force-pay-btn")?.click();

    }

  }, [shouldTriggerPayment]);

  const handlePaymentClick = async () => {
    if (localStorage.getItem('userStatus') === 'anonymous') { showAnonymousModal() }
    else { await handlePayment() }
  }

  // Affichage d'erreur si problème avec la commission
  if (commissionError) {
    console.warn("Erreur de commission:", commissionError)
  }

  const warningMessage = `Attention :  Dans certains cas, le traitement du paiement peut prendre un certain temps. Nous vous recommandons vivement de ne pas quitter ou actualiser la page durant cette étape. Cela pourrait perturber le processus de paiement et entraîner la perte des informations saisies, vous obligeant à recommencer la rédaction de votre annonce. Merci de votre compréhension.`

  if (commissionLoading) {
    return (
      <div className="payment-form-container text-center">
        <div className="border1 p-5 rounded shadow unique">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3">Chargement des informations de paiement...</p>
        </div>
      </div>
    )
  }

  if (hasFreeAnnouncement && processingFreeAnnouncement) {
    return (
      <div className="payment-form-container text-center">
        <div className="border1 p-5 rounded shadow unique">
          <h3>Création de votre annonce gratuite en cours...</h3>
          <div className="spinner-border text-primary mt-4" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-4">Veuillez patienter pendant que nous traitons votre annonce gratuite.</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="payment-form-container">
        {onBack && (
          <div
            className="payment-back"
            onClick={onBack}
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              marginBottom: "10px",
              color: "#2a919d",
              fontWeight: 500,
              fontFamily: "Mulish, sans-serif",
              fontStyle: "bold",
            }}
          >
            <span style={{ fontSize: 20, marginRight: 8 }}>←</span>
            Revenir à la validation
          </div>
        )}

        <form className="payment-form__form">
          <>
            <div className="row">
              <div className="mb-4 text-center payements">
                <h3>
                  {hasFreeAnnouncement ? "Création d'annonce gratuite" : "Recevez votre justificatif dès le paiement"}
                </h3>
              </div>
              {showAlert && <Alert message={warningMessage} type="warning" closable style={{ marginBottom: 20 }} />}
              <div className="col-sm-3 mt-2 mb-4">
                <label htmlFor="amount">Montant (EURO):</label>
              </div>
              <div className="col-sm-6">
                <input
                  id="amount"
                  type="text"
                  className="form-control"
                  value={hasFreeAnnouncement ? "0.00" : amount}
                  required
                  max="999999.99"
                  disabled
                />
              </div>
            </div>

            {hasActiveCommission() && commission?.commissionPercentage && text && (
              <div className="anim-loyalty-commission-container mb-4">
                <motion.div
                  className="anim-loyalty-commission-alert"
                  initial={{ scale: 0.9, opacity: 0, y: 20 }}
                  animate={{ scale: 1, opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                >
                  <div className="anim-content-wrapper">
                    <motion.div
                      className="anim-icon-container"
                      initial={{ rotate: -180, scale: 0 }}
                      animate={{ rotate: 0, scale: 1 }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                        <path
                          fill="currentColor"
                          d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7h16zm0-2H4V7a2 2 0 0 1 2-2h3.5c.97 0 1.84.44 2.44 1.17L12 8l.06-.83c.6-.73 1.47-1.17 2.44-1.17H18a2 2 0 0 1 2 2v3zM12 10.5l-1.5-2.25L9 10.5H12zm2.5-2.25L12 10.5h3l-1.5-2.25z"
                        />
                      </svg>
                    </motion.div>
                    <motion.div
                      className="anim-text-content"
                      key={animationKey}
                      variants={typewriterVariants}
                      initial="hidden"
                      animate="visible"
                    >
                      {(() => {
                        const percentageStr = commission.commissionPercentage.toString()
                        const percentageStart = text.indexOf(percentageStr)
                        const percentageEnd = percentageStart + percentageStr.length
                        return text.split("").map((char, index) => {
                          const isInPercentage = index >= percentageStart && index < percentageEnd
                          return (
                            <motion.span
                              key={`${animationKey}-${index}`}
                              variants={letterVariants}
                              className={isInPercentage ? "anim-highlight-percentage" : ""}
                            >
                              {char === " " ? "\u00A0" : char}
                            </motion.span>
                          )
                        })
                      })()}
                    </motion.div>
                    <motion.div
                      className="anim-pulse-indicator"
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.7, 1, 0.7],
                      }}
                      transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
                    />
                  </div>
                </motion.div>
              </div>
            )}

            {!hasFreeAnnouncement && !pointsLoading && pointsToEarn > 0 && (
              <PointsRewardAnimation
                pointsToEarn={pointsToEarn}
                isVisible={true}
              />
            )}

            {!hasFreeAnnouncement && (
              <div className="row">
                <div className="col-sm-3">
                  {/* <label>Détails de la carte:</label> */}
                </div>
                <div className="col-sm-12">
                  <PaymentElement className="form-control w-100 mb-2 card-element p-3"
                    onChange={(event) => {
                      setIsPaymentElementComplete(event.complete);
                    }} />
                  <div className="card-icons d-flex mt-2">
                    <img src={visa || "/placeholder.svg"} alt="Visa" className="card-icon" />
                    <img src={master || "/placeholder.svg"} alt="Mastercard" className="card-icon" />
                    <img src={amex || "/placeholder.svg"} alt="American Express" className="card-icon" />
                  </div>
                </div>
              </div>
            )}

          </>

          {isWaitingValidation && (
            <Alert
              type="info"
              showIcon
              message="Paiement en attente de validation"
              description="Votre paiement a été initié. Veuillez confirmer l'opération dans votre application bancaire ou via le système 3D Secure. Vous serez automatiquement redirigé une fois la validation effectuée."
              style={{ marginBottom: 20 }}
            />
          )}


          <button
            id="force-pay-btn"
            type="button"
            onClick={handlePaymentClick}
            className="payment-form__btn"
            disabled={!hasFreeAnnouncement && (!stripe || loading || isWaitingValidation || !isPaymentElementComplete)}
          >
            {loading ? (
              <div className="payment-form__btn-loading">
                <span>Chargement</span>
                <div className="dots-loader"></div>
              </div>
            ) : (
              <span>
                {hasFreeAnnouncement ? "Créer l'annonce gratuitement" : "Payer"}
              </span>
            )}
          </button>

          <button
            onClick={onBack}
            className="payment-form__btn payment-form__btn--back"
          >
            Revenir à la validation
          </button>
        </form>
      </div>

      <AnonymousModalPaiement
        isModalVisible={isModalVisible}
        handleModalClose={handleModalClose}
        userDetails={userDetails}
        setUserDetails={setUserDetails}
        handlePayment={handlePayment}
        isDraft={false}
      />
    </>
  )
}

export default Payment
